import { Music4, Image as ImageIcon, FileText, Globe, Heart, PiggyBank, HeartPulse,
  GraduationCap, PencilLine, ListChecks, Lightbulb, BookOpen, PenTool, Fingerprint,
  ScanSearch, Wand2, Images, Baby, Wine, Zap, Feather, MapPin, Sparkles } from "lucide-react";

// Everything MiLu can be: the tool tiles, the personas, the tutors and the story modes.
export const EXPLORE_TOOLS = [
  { id: "music", label: "Music Generation", to: "/tools/music", icon: Music4, image: "/tiles/music.jpg",
    blurb: "Compose a track from a prompt" },
  { id: "image", label: "AI Image Generator", to: "/tools/image", icon: ImageIcon, image: "/tiles/image.jpg",
    blurb: "Create artwork in any style" },
  { id: "summarizers", label: "AI Summarizers", to: "/tools/summarize", icon: FileText, image: "/tiles/summarize.jpg",
    blurb: "Images, PDFs and web pages" },
];

export const ELITE_TOOLS = [
  { id: "image", label: "AI Image Generator", to: "/tools/image", icon: ImageIcon, image: "/tiles/image.jpg",
    blurb: "Create artwork in any style" },
  { id: "logo", label: "AI Logo Maker", to: "/tools/logo", icon: PenTool, image: "/tiles/logo.jpg",
    blurb: "A brand mark from your idea" },
  { id: "tattoo", label: "AI Tattoo", to: "/tools/tattoo", icon: Fingerprint, image: "/tiles/tattoo.jpg",
    blurb: "Design your dream tattoo" },
  { id: "detector", label: "AI Detector", to: "/tools/detector", icon: ScanSearch, image: "/tiles/detector.jpg",
    blurb: "Is this text AI or human?" },
  { id: "creations", label: "My Creations", to: "/creations", icon: Images, image: "/tiles/creations.jpg",
    blurb: "Everything you've generated" },
];

export const PERSONAS = [
  { id: "general", label: "General" },
  { id: "explore", label: "Explore" },
  { id: "assistant", label: "Assistant" },
  { id: "tutor", label: "Tutor" },
  { id: "elite", label: "Elite Tools" },
  { id: "storyteller", label: "Storytelling" },
];

export const ASSISTANTS = [
  { id: "web_owl", name: "Web Owl", icon: Globe, avatar: "/avatars/web_owl.jpg",
    greeting: "Hey! Let's make a difference together! Go ahead and ask anything — paste a link and I'll read the real page for you.",
    starters: ["Summarise this link for me: ", "What's new in AI this month?", "Compare two products for me"] },
  { id: "dating_advisor", name: "Dating Advisor", icon: Heart, avatar: "/avatars/dating_advisor.jpg",
    greeting: "Hello! Let's dive into the world of dating and relationships. What's on your mind?",
    starters: ["How do I open a conversation on a dating app?", "Ideas for a great first date", "How do I know they're interested?"] },
  { id: "financial_assistant", name: "Financial Assistant", icon: PiggyBank, avatar: "/avatars/financial_assistant.jpg",
    greeting: "Hello! Curious about managing your finances, investments, or planning for the future? Let's address all financial issues now!",
    starters: ["Build me a simple monthly budget", "How do I start an emergency fund?", "Explain index funds in plain words"] },
  { id: "wellness_advisor", name: "Wellness Advisor", icon: HeartPulse, avatar: "/avatars/wellness_advisor.jpg",
    greeting: "Hello! I'm here to listen and provide support. Please feel free to share any problems or concerns you have, and I'll do my best to help you.",
    starters: ["I feel stressed and can't switch off", "Help me sleep better", "How do I build a habit that sticks?"] },
  { id: "study_helper", name: "Study & Work Helper", icon: GraduationCap, avatar: "/avatars/study_helper.jpg",
    greeting: "Hi! How can I help with your studies or career?",
    starters: ["Make me a study plan for next week", "Improve my CV summary", "Prepare me for a job interview"] },
];

export const TUTOR_MODES = [
  { id: "homework_help", name: "Get homework help", icon: PencilLine, image: "/tiles/homework.jpg",
    blurb: "Learn smarter with interactive sessions designed to support and educate",
    greeting: "Let's work through it together — send me the question and we'll solve it step by step, no shortcuts.",
    starters: ["Help me solve this maths problem", "Explain this homework question", "Check my answer and explain why"] },
  { id: "test_prep", name: "Prepare for tests", icon: ListChecks, image: "/tiles/testprep.jpg",
    blurb: "Test your knowledge with quizzes and open-ended questions",
    greeting: "Tell me the subject and level, and I'll quiz you — mixing multiple-choice and open questions, marking each answer as we go.",
    starters: ["Quiz me on world history", "Give me 5 practice questions on algebra", "Test my English vocabulary"] },
  { id: "explore_subjects", name: "Explore new subjects", icon: Lightbulb, image: "/tiles/explore.jpg",
    blurb: "Master any topic through questions, hints, and step-by-step explanations",
    greeting: "Name any topic and I'll take you from zero: a plain-language overview, the core ideas, then hints and step-by-step explanations.",
    starters: ["Teach me the basics of astronomy", "Explain how the economy works", "Introduce me to Vietnamese history"] },
];

export const STORY_MODES = [
  { id: "story_everyone", name: "Stories for Everyone", icon: BookOpen, avatar: "/avatars/story_everyone.jpg",
    blurb: "Create stories without age restrictions",
    starters: ["A lighthouse keeper who finds a message in a bottle", "Two strangers stuck on a night train", "A village where it rains only on Sundays"],
    greeting: "Greetings! Ready for a bit of storytelling wonder? To create the perfect story, I only need you to answer two simple questions. First, who do you see as the main character?" },
  { id: "story_kids", name: "Stories for Kids", icon: Baby, avatar: "/avatars/story_kids.jpg",
    blurb: "Generate stories that children will enjoy",
    starters: ["A shy hedgehog called Pip", "A puppy who is scared of the dark", "A little girl who can talk to clouds"],
    greeting: "Greetings! Ready for a bit of storytelling wonder? To create the perfect story, I only need you to answer two simple questions. First, who do you see as the main character?" },
  { id: "story_adults", name: "Stories for Adults", icon: Wine, avatar: "/avatars/story_adults.jpg",
    blurb: "Write stories for grown-up readers",
    starters: ["A chef returning to the town she left at 18", "A quiet betrayal between old friends", "The last night of a failing bookshop"],
    greeting: "Greetings! Ready for a bit of storytelling wonder? To create the perfect story, I only need you to answer three simple questions. First, who do you see as the main character?" },
  { id: "story_comic", name: "Comic Books", icon: Zap, avatar: "/avatars/story_comic.jpg",
    blurb: "Make comic-style stories",
    starters: ["A courier robot in a rainy neon city", "A teen who wakes up with gravity powers", "A detective cat solving a museum heist"],
    greeting: "Greetings! Ready for a bit of storytelling wonder? To create the perfect comic book, I only need you to answer two simple questions. First, who do you see as the main character?" },
  { id: "story_descriptive", name: "Descriptive Stories", icon: Feather, avatar: "/avatars/story_descriptive.jpg",
    blurb: "Get detailed descriptions of any thing",
    starters: ["Describe a violin left in an attic for 40 years", "Describe a storm arriving over the sea", "Describe my grandmother's kitchen at dawn"],
    greeting: "Greetings! Ready for a bit of storytelling wonder? What would you like me to describe in detail?" },
  { id: "story_location", name: "Location Descriptions", icon: MapPin, avatar: "/avatars/story_location.jpg",
    blurb: "Have any place described",
    starters: ["Describe Hanoi at 6am", "Describe a fishing village in Portugal", "Describe Tokyo in the rain"],
    greeting: "Greetings! Ready for a bit of storytelling wonder? Which place or city would you like me to describe?" },
];

export const CATALOG = [
  { id: "elite", label: "Elite Tools", tab: "elite",
    blurb: "Studio-grade logos, tattoos and AI detection", items: ELITE_TOOLS },
  { id: "storyteller", label: "Storytelling", tab: "storyteller",
    blurb: "Stories, comics and vivid descriptions", items: STORY_MODES },
  { id: "explore", label: "Explore", tab: "explore",
    blurb: "Generators for music, images and instant summaries", items: EXPLORE_TOOLS },
  { id: "assistant", label: "Assistants", tab: "assistant",
    blurb: "Specialists who answer in your language", items: ASSISTANTS },
  { id: "tutor", label: "Tutor", tab: "tutor",
    blurb: "Homework help, test prep and new subjects", items: TUTOR_MODES },
];

export const FLAT_CATALOG = CATALOG.flatMap((group) =>
  group.items.map((item) => ({ key: `${group.id}:${item.id}`, group, item }))
);

export const ALL_MODES = [...ASSISTANTS, ...TUTOR_MODES, ...STORY_MODES];

export const parentPersona = (id) => {
  if (STORY_MODES.some((m) => m.id === id)) return "storyteller";
  if (TUTOR_MODES.some((m) => m.id === id)) return "tutor";
  if (ASSISTANTS.some((m) => m.id === id)) return "assistant";
  return id;
};

// persona id -> { label, category } so history rows can show the prompt they came from.
export const PROMPT_META = {
  ...Object.fromEntries(PERSONAS.map((p) => [p.id, { label: p.label, category: p.id }])),
  ...Object.fromEntries(ALL_MODES.map((m) => [m.id, { label: m.name, category: parentPersona(m.id) }])),
};
